import type { PostMedia } from '../../../types/publishing';

// Publishing previews — render a post's uploaded media (a public Zernio URL, not a
// Drive iframe). Image → <img>; video → a real <video controls> (playable inline,
// the inbox/ad-preview pattern for a public URL). First media item only for the
// single-media Phase-1 previews (carousel is Phase 2).
//
// `fit` (item 3, 2026-07-28):
//   'cover'   — crop to fill a FIXED-aspect frame (the default). A STORY / REEL is
//               genuinely a fixed 9:16 surface Meta crops to fill, and the tiny
//               calendar-hover thumbnail wants a clean square fill.
//   'contain' — fit the WHOLE asset inside a fixed frame (letterboxed). Kept for
//               callers that still want a bounded box.
//   'natural' — NO fixed frame: the container sizes to the image's own aspect
//               ratio (w-full, h-auto), so a portrait image makes a taller card
//               and a landscape one a shorter card, exactly like the real feed —
//               and there are NO letterbox bars in the normal case. An extreme
//               portrait is capped at MAX (~viewport height), past which it falls
//               back to contain within that cap. This is what the FEED previews
//               (FB/IG feed + GBP) use — feeds accept varied aspect ratios, so the
//               preview must show the true image edge to edge.
// Default is 'cover' so no existing consumer regresses.

export type MediaFit = 'cover' | 'contain' | 'natural';

export function PostMediaView({
  media,
  aspectClass,
  fit = 'cover',
}: {
  media: PostMedia[];
  // The fixed frame for 'cover'/'contain', AND the placeholder box shape when
  // there's no media (natural mode borrows it only for the empty state).
  aspectClass: string;
  fit?: MediaFit;
}) {
  const first = media[0];
  if (!first) {
    return (
      <div className={`${aspectClass} w-full bg-slate-100 flex items-center justify-center`}>
        <p className="text-xs text-slate-400">No media</p>
      </div>
    );
  }

  // Natural mode — size to the asset, no fixed box, no bars. object-contain +
  // max-h only matters for the extreme-portrait cap; below the cap the box already
  // matches the intrinsic ratio so nothing is cropped or padded.
  if (fit === 'natural') {
    const naturalClass = 'block w-full h-auto max-h-[85vh] object-contain';
    return (
      <div className="w-full overflow-hidden bg-slate-100">
        {first.type === 'video' ? (
          <video
            src={first.url}
            poster={first.thumbnailUrl ?? undefined}
            controls
            playsInline
            className={naturalClass}
          />
        ) : (
          <img
            src={first.url}
            alt={first.altText || first.filename || 'post media'}
            loading="lazy"
            className={naturalClass}
          />
        )}
      </div>
    );
  }

  const objectClass = fit === 'contain' ? 'object-contain' : 'object-cover';
  if (first.type === 'video') {
    return (
      <div className={`${aspectClass} w-full overflow-hidden bg-black`}>
        <video
          src={first.url}
          poster={first.thumbnailUrl ?? undefined}
          controls
          playsInline
          className={`w-full h-full ${objectClass}`}
        />
      </div>
    );
  }
  return (
    <div className={`${aspectClass} w-full overflow-hidden bg-slate-900`}>
      <img
        src={first.url}
        alt={first.altText || first.filename || 'post media'}
        loading="lazy"
        className={`w-full h-full ${objectClass}`}
      />
    </div>
  );
}

// Small circular avatar with the page/brand initial (decorative, mirrors the ad
// preview mockups).
export function BrandCircle({ name }: { name: string }) {
  const initial = name.trim().charAt(0).toUpperCase() || '•';
  return (
    <div className="h-9 w-9 rounded-full bg-slate-300 text-slate-900 flex items-center justify-center text-sm font-semibold shrink-0">
      {initial}
    </div>
  );
}
